// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import VideoPauseButton from "@synd/XDL/Icons/VideoPauseButton";
import VideoPlayButton from "@synd/XDL/Icons/VideoPlayButton";
import { color, getVideoEmbeddedUrl } from "@synd/XDL/Utilities";
import React, { useRef, useState } from "react";
import styled from "styled-components";

const ControlButton = styled.button<{ isPlaying?: boolean }>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-radius: 50%;
    border: none;
    background: transparent;
    color: ${color("common.ui9")};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    z-index: 10;

    ${({ isPlaying }) =>
     isPlaying
            ? `
        opacity: 0;
        pointer-events: none;
    `
            : `
        opacity: 1;
        pointer-events: auto;
    `}

    &:hover {
        transform: translate(-50%, -50%) scale(1.05);
    }

    &:active {
        transform: translate(-50%, -50%) scale(0.95);
    }
`;

const VideoContainer = styled.div`
    position: relative;
    width: 100%;
    height: 100%;

    &:hover ${ControlButton} {
        opacity: 1;
        pointer-events: auto;
    }
`;

export interface VideoProps {
    src: string;
    controls?: boolean;
    extension?: string;
    autoPlay?: boolean;
    muted?: boolean;
    repeat?: boolean;
    preload?: "none" | "metadata" | "auto";
    size?: number;
}

type Props = VideoProps;

const Video: React.FC<Props> = ({
    src,
    controls,
    muted,
    extension = "mp4",
    autoPlay,
    repeat,
    preload = "none",
    size = 36,
}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const [isPlaying, setIsPlaying] = useState(autoPlay);

    const playVideo = () => {
        videoRef
            .current!.play()
            .then(() => setIsPlaying(true))
            .catch((e: Error) => {
                console.error("error playing video", e);
                setIsPlaying(false);
            });
    };

    const pauseVideo = () => {
        videoRef.current!.pause();
        setIsPlaying(false);
    };

    const togglePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                pauseVideo();
            } else {
                playVideo();
            }
        }
    };

    if (!src) {
        return null;
    }

    const withExtension = src.includes(extension);
    const embeddedURL = !withExtension ? getVideoEmbeddedUrl(src) : "";

    return withExtension ? (
        <VideoContainer>
            <video
                key={src}
                ref={videoRef}
                width="100%"
                height="100%"
                controls={controls}
                loop={repeat}
                autoPlay={autoPlay}
                muted={muted}
                className="lazyload"
                preload={preload}
            >
                <track kind="captions" src="sampleCaptions.vtt" srcLang="en" />
                <source src={src} type={`video/${extension}`}></source>
            </video>
            <ControlButton onClick={togglePlayPause} aria-label="Pause" isPlaying={isPlaying}>
                {isPlaying ? <VideoPauseButton width={size} height={size} /> : <VideoPlayButton size={size} />}
            </ControlButton>
        </VideoContainer>
    ) : (
        <iframe
            src={embeddedURL}
            allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
            allowFullScreen
            width="100%"
            height="100%"
            style={{ border: "unset" }}
            loading="lazy"
        />
    );
};

export default Video;
